import Cookies from 'cookies-js';

class Factory {

  constructor(ClassImporter, properties, locale, redirectUrl, trackingContext, dataLayer, app) {

    this.ClassImporter = ClassImporter;
    this.verificationServiceUrl = properties.verification_url;
    this.dictionaryServiceUrl = properties.dictionary_url;
    this.authenticationServiceUrl = properties.auth_url;
    this.openIdServiceUrl = properties.openid_url;
    this.locale = locale;
    this.redirectUrl = redirectUrl;
    this.trackingContext = trackingContext;
    this.dataLayer = dataLayer;
    this.app = app;

  }

  initialiseFactory() {

    this.getController();
    return this.getPlaceholderService().initialisePlaceholderService()
      .then(() => {
        return this.getTranslationService().initialiseTranslationService();
      });

  }

  // Controller

  getController() {

    if (typeof this.controller !== 'object') {
      this.controller = new this.ClassImporter.Controller(this.app, this);
      this.controller.initialiseController();
    }
    return this.controller;

  }

  // Services

  getViewBuilder() {

    if (typeof this.viewBuilder !== 'object') {
      this.viewBuilder = new this.ClassImporter.ViewBuilder(this.getController(), this.getTranslationService(), this.getLocaleService() );
    }
    return this.viewBuilder;

  }

  getUrlGenerator() {

    if (typeof this.urlGenerator !== 'object') {
      this.urlGenerator = new this.ClassImporter.UrlGenerator(this.getLocaleService(), this.redirectUrl, this.verificationServiceUrl, this.dictionaryServiceUrl, this.authenticationServiceUrl, this.openIdServiceUrl);
    }
    return this.urlGenerator;

  }

  getRequestBuilder() {

    if (typeof this.requestBuilder !== 'object') {
      this.requestBuilder = new this.ClassImporter.RequestBuilder(this.getUrlGenerator(), this.getLocaleService());
    }
    return this.requestBuilder;

  }

  getPlaceholderService() {

    if (typeof this.placeholderService !== 'object') {
      this.placeholderService = new this.ClassImporter.PlaceholderService(this.getHttpService(), this.getRequestBuilder());
    }
    return this.placeholderService;

  }

  getTranslationMapper() {

    if (typeof this.translationMapper !== 'object') {
      this.translationMapper = new this.ClassImporter.TranslationMapper(this.trackingContext);
    }
    return this.translationMapper;

  }

  getTranslationService() {

    if (typeof this.translationService !== 'object') {
      this.translationService = new this.ClassImporter.TranslationService(this.getPlaceholderService(), this.getTranslationMapper(), this.getHttpService(), this.getRequestBuilder());
    }
    return this.translationService;

  }

  getTrackingService() {

    if (typeof this.trackingService !== 'object') {
      this.trackingService = new this.ClassImporter.TrackingService(this.dataLayer, this.trackingContext);
    }
    return this.trackingService;

  }

  getCookieUtils() {

    return Cookies;

  }

  getSocialIconsLoaderService() {

    if (typeof this.socialIconsLoaderService !== 'object') {
      this.socialIconsLoaderService = new this.ClassImporter.SocialIconsLoaderService(this.getController());
    }
    return this.socialIconsLoaderService;

  }

  // HTTP

  getFetchExecutor() {

    if (typeof this.fetchExecutor !== 'object') {
      this.fetchExecutor = new this.ClassImporter.FetchExecutor(this.getHttpResponseHandler());
    }
    return this.fetchExecutor;

  }

  getHttpService() {

    if (typeof this.httpService !== 'object') {
      this.httpService = new this.ClassImporter.HttpService(this.getFetchExecutor());
    }
    return this.httpService;

  }

  getHttpResponseHandler() {

    if (typeof this.httpResponseHandler !== 'object') {
      this.httpResponseHandler = new this.ClassImporter.HttpResponseHandler(this.getUrlGenerator());
    }
    return this.httpResponseHandler;

  }

  // Validators

  getValidatorUtils() {

    if (typeof this.validatorUtils !== 'object') {
      this.validatorUtils = new this.ClassImporter.ValidatorUtils();
    }
    return this.validatorUtils;

  }

  getEmailAddressValidator() {

    if (typeof this.emailAddressValidator !== 'object') {
      this.emailAddressValidator = new this.ClassImporter.EmailAddressValidator(this.getValidatorUtils());
    }
    return this.emailAddressValidator;

  }

  getLoginValidator() {

    if (typeof this.loginValidator !== 'object') {
      this.loginValidator = new this.ClassImporter.LoginValidator(this.getValidatorUtils());
    }
    return this.loginValidator;

  }

  getLinkAccountValidator() {

    if (typeof this.linkAccountValidator !== 'object') {
      this.linkAccountValidator = new this.ClassImporter.LinkAccountValidator(this.getValidatorUtils());
    }
    return this.linkAccountValidator;

  }

  getRegisterValidator() {

    if (typeof this.registerValidator !== 'object') {
      this.registerValidator = new this.ClassImporter.RegisterValidator(this.getValidatorUtils());
    }
    return this.registerValidator;

  }

  // Handlers

  getErrorHandler() {

    if (typeof this.errorHandler !== 'object') {
      this.errorHandler = new this.ClassImporter.ErrorHandler(this.getController(), this.getTranslationService());
    }
    return this.errorHandler;

  }

  getEmailCaptureHandler() {

    if (typeof this.emailCaptureHandler !== 'object') {
      this.emailCaptureHandler = new this.ClassImporter.EmailCaptureHandler(
        this.getController(),
        this.getEmailAddressValidator(),
        this.getRequestBuilder(),
        this.getHttpService(),
        this.getTrackingService(),
        this.getCookieUtils(),
        this.ClassImporter.Utils,
        this.getLocaleService()
      );
    }
    return this.emailCaptureHandler;

  }

  getLoginHandler() {

    if (typeof this.loginHandler !== 'object') {
      this.loginHandler = new this.ClassImporter.LoginHandler(
        this.getController(),
        this.getLoginValidator(),
        this.getRequestBuilder(),
        this.getHttpService(),
        this.getTrackingService(),
        this.getCookieUtils(),
        this.ClassImporter.Utils
      );
    }
    return this.loginHandler;

  }

  getOpenIdLoginHandler() {

    if (typeof this.openIdLoginHandler !== 'object') {
      this.openIdLoginHandler = new this.ClassImporter.OpenIdLoginHandler(
        this.getController(),
        this.getRequestBuilder(),
        this.getHttpService(),
        this.getTrackingService(),
        this.getCookieUtils(),
        this.ClassImporter.Utils
      );
    }
    return this.openIdLoginHandler;

  }

  getResetPasswordHandler() {

    if (typeof this.resetPasswordHandler !== 'object') {
      this.resetPasswordHandler = new this.ClassImporter.ResetPasswordHandler(
        this.getController(),
        this.getEmailAddressValidator(),
        this.getRequestBuilder(),
        this.getHttpService(),
        this.getTrackingService()
      );
    }
    return this.resetPasswordHandler;

  }

  getLinkAccountHandler() {

    if (typeof this.linkAccountHandler !== 'object') {
      this.linkAccountHandler = new this.ClassImporter.LinkAccountHandler(
        this.getController(),
        this.getLinkAccountValidator(),
        this.getRequestBuilder(),
        this.getHttpService(),
        this.getTrackingService(),
        this.getCookieUtils(),
        this.ClassImporter.Utils
      );
    }
    return this.linkAccountHandler;

  }

  getRegisterHandler() {

    if (typeof this.registerHandler !== 'object') {
      this.registerHandler = new this.ClassImporter.RegisterHandler(
        this.getController(),
        this.getRegisterValidator(),
        this.getRequestBuilder(),
        this.getHttpService(),
        this.getTrackingService(),
        this.getCookieUtils(),
        this.ClassImporter.Utils
      );
    }
    return this.registerHandler;

  }

  getLocaleService(){

      if (typeof this.localeService !== 'object') {
          this.localeService = new this.ClassImporter.LocaleService(this.locale);
      }
      return this.localeService;
  }

}

export default Factory;
